import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@env';
import { catchError } from 'rxjs';
import { Observable, throwError } from 'rxjs';
import { ErrorHandlerService } from './response-utils.service';
import {
  ProductListResponse,
  ProductVersionInfo,
  ReleaseNoteResponse,
  ReleaseNotes,
} from '@components/release-notes';

@Injectable({ providedIn: 'root' })
export class ReleaseNotesService {
  private readonly URL: string = environment.apiUrl + '/product';

  private http = inject(HttpClient);
  private errorHandler = inject(ErrorHandlerService);

  uploadToPresignedUrl(presignedUrl: string, file: File): Observable<{ status: number }> {
    return new Observable((observer) => {
      const cleanUrl = presignedUrl.replace(/"/g, '');
      const xhr = new XMLHttpRequest();

      xhr.open('PUT', cleanUrl, true);
      xhr.setRequestHeader('Content-Type', file.type);

      xhr.onload = () => {
        if (xhr.status === 200) {
          observer.next({ status: 200 });
          observer.complete();
        } else {
          observer.error({ status: xhr.status, message: xhr.statusText });
        }
      };

      xhr.onerror = () => {
        observer.error({ status: xhr.status, message: 'Upload failed' });
      };

      xhr.send(file);
    });
  }

  getURLFromS3(releaseNotes: ProductVersionInfo) {
    const url = this.URL + '/releases/upload/init';
    return this.http
      .post(url, releaseNotes)
      .pipe(catchError((error: HttpErrorResponse) => this.errorHandler.errorHandler(error)));
  }

  getAllProductList(): Observable<ProductListResponse> {
    return this.http.get<ProductListResponse>(this.URL + '/releases/products').pipe(
      catchError((error: HttpErrorResponse) => {
        this.errorHandler.errorHandler(error);
        return throwError(() => error);
      }),
    );
  }

  createReleaseNotes(jsonData: ReleaseNotes) {
    return this.http
      .post(this.URL + '/releases', jsonData)
      .pipe(catchError((error: HttpErrorResponse) => this.errorHandler.errorHandler(error)));
  }

  updateReleaseNotes(jsonData: ReleaseNotes) {
    return this.http
      .put(this.URL + '/releases', jsonData)
      .pipe(catchError((error: HttpErrorResponse) => this.errorHandler.errorHandler(error)));
  }

  getAllReleaseNotes(
    page: number,
    rows: number,
    sortBy: string,
    sortDir: string,
    search: string,
  ): Observable<ReleaseNoteResponse> {
    let url =
      this.URL +
      `/releases/author?rowsPerPage=${rows}&page=${page}&sortBy=${sortBy}&sortDirection=${sortDir}`;
    if (search) {
      url = url + `&search=${search}`;
    }
    return this.http.get<ReleaseNoteResponse>(url).pipe(
      catchError((error: HttpErrorResponse) => {
        this.errorHandler.errorHandler(error);
        return throwError(() => error);
      }),
    );
  }

  getReleaseNotesById(id: string): Observable<ReleaseNotes> {
    return this.http.get<ReleaseNotes>(this.URL + '/releases/' + id).pipe(
      catchError((error: HttpErrorResponse) => {
        this.errorHandler.errorHandler(error);
        return throwError(() => error);
      }),
    );
  }

  deleteById(id: string): Observable<ReleaseNotes> {
    return this.http.delete<ReleaseNotes>(this.URL + '/releases/' + id).pipe(
      catchError((error: HttpErrorResponse) => {
        this.errorHandler.errorHandler(error);
        return throwError(() => error);
      }),
    );
  }

  publishById(id: string): Observable<ReleaseNotes> {
    return this.http.post<ReleaseNotes>(this.URL + '/releases/' + id + '/publish', {}).pipe(
      catchError((error: HttpErrorResponse) => {
        this.errorHandler.errorHandler(error);
        return throwError(() => error);
      }),
    );
  }

  fetchFileAsBlob(url: string): Observable<Blob> {
    return new Observable((observer) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.responseType = 'blob';

      xhr.onload = () => {
        if (xhr.status === 200) {
          observer.next(xhr.response);
          observer.complete();
        } else {
          observer.error({ status: xhr.status, message: xhr.statusText });
        }
      };

      xhr.onerror = () => {
        observer.error({ status: xhr.status, message: 'Fetch failed' });
      };

      xhr.send();
    });
  }
}
